import React, { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CookieConsent from '@/components/CookieConsent';
import FloatingActions from '@/components/FloatingActions';
import InstallPrompt from '@/components/InstallPrompt';
// Every route opens at the top (Pricing, Membership, Dashboard, Legal...),
// except Back/Forward, a return to browse results, and the language toggle.
import ScrollToTop from '@/components/ScrollToTop';
import { useI18n } from '@/i18n/LanguageContext';
import { trackPageView } from '@/lib/tracking';
import ResumePurchaseBanner from '@/components/ResumePurchaseBanner';
import TrainingBanner from '@/components/TrainingBanner';
import ChatWidgetGate from '@/components/ChatWidgetGate';
import RoleQuickStart from '@/components/home/RoleQuickStart';
import ShareButton from '@/components/ShareButton';

/**
 * PUBLIC LANDING PAGES that get the three role doors at the very top, so a
 * visitor arriving from search or a shared link sees "Homeowner / Contractor /
 * Subcontractor" before anything else — the same component as the home hero.
 * Deliberately NOT on: home (doors live in the hero), the marketplace and lead
 * detail (the content IS the action), and every private / checkout / auth page.
 */
const ROLE_BAR_SECTIONS = new Set([
  'homeowners', 'propietarios', 'general-contractors', 'contratistas-generales', 'subcontractors', 'subcontratistas',
  'commercial', 'comercial', 'post-a-project', 'publicar-proyecto', 'trades', 'oficios', 'states', 'estados',
  'california', 'leads', 'resources', 'recursos', 'blog', 'project-directory', 'directorio-de-proyectos',
  'contractor-directory', 'directorio-de-contratistas', 'about', 'acerca-de', 'memberships', 'membresias',
  'faq', 'preguntas-frecuentes', 'help', 'ayuda', 'how-it-works', 'como-funciona', 'pricing', 'precios',
  'testimonials', 'testimonios', 'reviews', 'contact', 'contacto',
]);

const SiteShell: React.FC = () => {
  const { fading, lang } = useI18n();
  const location = useLocation();
  const last = useRef<string>('');
  const segs = location.pathname.split('/').filter(Boolean);
  const section = segs[1] || '';
  const showRoleBar = ROLE_BAR_SECTIONS.has(section);
  // Project / lead detail pages: /en/jobs/<slug>, /es/trabajos/<slug>.
  const isDetail = (section === 'jobs' || section === 'trabajos') && segs.length >= 3;

  // Anonymous, first-party page-view tracking. No fingerprinting, no third
  // party pixels — see src/lib/tracking.ts. Fire-and-forget so navigation is
  // never delayed by the tracking call.
  useEffect(() => {
    const key = location.pathname + location.search;
    if (last.current === key) return;
    last.current = key;
    trackPageView();
  }, [location.pathname, location.search]);

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      {/* Must sit INSIDE the router: a new route opens at the top unless the
          navigation is a Back/Forward, a return to browse results, or the
          language toggle. See src/components/ScrollToTop.tsx. */}
      <ScrollToTop />
      {/* TEST/TRAINING accounts only (mkt_test_accounts). Renders nothing for real users. */}
      <TrainingBanner />
      <Header />
      {/* "You were buying X — Continue" for anyone who drifted off mid-purchase. */}
      <ResumePurchaseBanner />

      {showRoleBar && (
        <div className="border-b border-slate-200 bg-gradient-to-b from-white to-slate-50">
          <RoleQuickStart variant="light" className="mx-auto max-w-7xl px-4 py-4 sm:px-6 sm:py-5 lg:px-8" />
        </div>
      )}

      {/* SHARE BAR on every shareable detail page (project / lead detail and the
          landing pages): the link is the canonical /en|es/... URL of THIS page,
          so the recipient lands directly on the same project — no redirect. */}
      {(isDetail || showRoleBar) && (
        <div className="mx-auto flex w-full max-w-7xl justify-end px-4 pt-3 sm:px-6 lg:px-8">
          <ShareButton
            to={location.pathname}
            title={typeof document !== 'undefined' ? document.title : 'GIGGONE'}
            text={isDetail ? (lang === 'es' ? 'Mire este proyecto en GIGGONE:' : 'Check out this project on GIGGONE:') : undefined}
          />
        </div>
      )}

      <main className={`flex-1 transition-opacity duration-200 ${fading ? 'opacity-0' : 'opacity-100'}`}>
        <Outlet />
      </main>
      <Footer />
      <CookieConsent />
      {/* Sticky conversion actions: Start Now (plan picker) + WhatsApp. */}
      <FloatingActions />
      {/* Hides the GoHighLevel chat launcher (loaded from index.html) on
          private routes, while any overlay/checkout is open, and while the
          sticky lead purchase bar is up. Renders nothing. */}
      <ChatWidgetGate />
      {/* "Add to Home Screen" — self-gating: renders nothing unless
          src/lib/pwa.ts says this visitor qualifies (2nd visit or a key
          action, not installed, not dismissed in the last 30 days). */}
      <InstallPrompt />
    </div>


  );
};

export default SiteShell;
